// Cookie consent management. Single source of truth used by both the
// CookieBanner component and any future analytics / marketing integration.
//
// Usage in any component:
//   import { useConsent } from "@/lib/consent";
//   const { consent } = useConsent();
//   if (consent.analytics) { /* load GA */ }

import { useEffect, useState } from "react";

const STORAGE_KEY = "mbw_cookie_consent_v1";
const CONSENT_EVENT = "mbw:consent-changed";

export const CATEGORIES = [
  {
    key: "essential",
    label: "Notwendig",
    description: "Für den Betrieb der Website zwingend erforderlich (z. B. Login-Session, CSRF-Schutz, Formularübertragung). Diese Cookies können nicht deaktiviert werden.",
    required: true,
  },
  {
    key: "functional",
    label: "Funktional",
    description: "Verbessern den Komfort der Seite: Spracheinstellung, gespeicherte Eingaben im Kalkulator, eingebettete Karten von OpenStreetMap.",
    required: false,
  },
  {
    key: "analytics",
    label: "Statistik",
    description: "Anonymisierte Nutzungsstatistiken (z. B. welche Seiten besucht werden), damit wir die Inhalte verbessern können. Keine Identifikation einzelner Personen.",
    required: false,
  },
  {
    key: "marketing",
    label: "Marketing",
    description: "Personalisierte Werbung und Erfolgsmessung von Kampagnen. Wird nur aktiviert, wenn Sie ausdrücklich zustimmen.",
    required: false,
  },
];

const DEFAULT_CONSENT = {
  essential: true,
  functional: false,
  analytics: false,
  marketing: false,
  version: 1,
  timestamp: null,
};

export function getConsent() {
  if (typeof window === "undefined") return { ...DEFAULT_CONSENT };
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed.version !== DEFAULT_CONSENT.version) return null;
    return { ...DEFAULT_CONSENT, ...parsed, essential: true };
  } catch {
    return null;
  }
}

export function saveConsent(partial) {
  const current = getConsent() || { ...DEFAULT_CONSENT };
  const next = {
    ...current,
    ...partial,
    essential: true,
    version: DEFAULT_CONSENT.version,
    timestamp: new Date().toISOString(),
  };
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: next }));
  } catch {
    // ignore storage failures (private mode etc.) – banner just keeps showing
  }
  return next;
}

export function clearConsent() {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
    window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: null }));
  } catch {
    /* noop */
  }
}

// Triggers the banner to reopen (used by footer "Cookie-Einstellungen" link).
export function openCookieSettings() {
  window.dispatchEvent(new CustomEvent("mbw:open-cookie-settings"));
}

// React hook — re-renders when consent changes.
export function useConsent() {
  const [consent, setConsent] = useState(() => getConsent() || DEFAULT_CONSENT);

  useEffect(() => {
    const handler = (e) => setConsent(e.detail || DEFAULT_CONSENT);
    window.addEventListener(CONSENT_EVENT, handler);
    return () => window.removeEventListener(CONSENT_EVENT, handler);
  }, []);

  return { consent, hasDecided: !!getConsent(), saveConsent, clearConsent };
}
